import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Container,
  Box,
  TextField,
  Typography,
  MenuItem,
  Chip,
  Grid,
  Card,
  CardContent,
  Button,
  Paper,
  Stack,
  Divider,
  IconButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import BuildIcon from "@mui/icons-material/Build";
import HandymanIcon from "@mui/icons-material/Handyman";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import VerticalAlignTopIcon from "@mui/icons-material/VerticalAlignTop";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SettingsRemoteIcon from "@mui/icons-material/SettingsRemote";
import TopNavBar from "../components/TopNavBar";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const GUIDE_ITEMS = [
  {
    id: "video-general-maintenance-overview",
    title: "Maintenance Video Overview",
    manufacturer: "general",
    category: "maintenance",
    difficulty: "basic",
    minutes: 8,
    summary:
      "Quick walkthrough video for routine maintenance checks and safe handling before service.",
    tools: ["None"],
    tags: ["video", "overview", "maintenance"],
    contentUrl: "https://www.youtube.com/watch?v=VQJDIhIXMkM",
    contentType: "video",
  },
  {
    id: "daily-lionsbot-brush-check",
    title: "Daily Brush and Roller Check",
    manufacturer: "lionsbot",
    category: "maintenance",
    difficulty: "basic",
    minutes: 10,
    summary:
      "Inspect and clean side brush, main roller and intake to prevent drag and reduced pickup.",
    tools: ["Microfiber cloth", "Brush comb"],
    tags: ["daily", "cleaning performance"],
  },
  {
    id: "weekly-lionsbot-filter-service",
    title: "Weekly Filter Service",
    manufacturer: "lionsbot",
    category: "maintenance",
    difficulty: "basic",
    minutes: 15,
    summary:
      "Clean and reseat filters to maintain suction performance and avoid overheating.",
    tools: ["Compressed air", "Dry cloth"],
    tags: ["weekly", "filter"],
  },
  {
    id: "bosch-docking-recovery",
    title: "Docking Recovery Procedure",
    manufacturer: "bosch",
    category: "troubleshooting",
    difficulty: "technician",
    minutes: 20,
    summary:
      "Step-by-step checks when the robot fails to dock or repeatedly aborts docking.",
    tools: ["Multimeter", "Isopropyl wipes"],
    tags: ["charging", "docking"],
  },
  {
    id: "bosch-wheel-module-replacement",
    title: "Wheel Module Replacement",
    manufacturer: "bosch",
    category: "repair",
    difficulty: "advanced",
    minutes: 35,
    summary: "Safe replacement workflow for worn or damaged wheel assemblies.",
    tools: ["Torx set", "Torque driver"],
    tags: ["mobility", "hardware"],
  },
  {
    id: "general-water-system-calibration",
    title: "Water System Calibration",
    manufacturer: "general",
    category: "calibration",
    difficulty: "technician",
    minutes: 25,
    summary:
      "Calibrate spray intervals and flow rates after maintenance or component changes.",
    tools: ["Calibration cup", "Service tablet"],
    tags: ["mop", "water system"],
  },
  {
    id: "general-battery-health-audit",
    title: "Battery Health Audit",
    manufacturer: "general",
    category: "maintenance",
    difficulty: "technician",
    minutes: 30,
    summary:
      "Run health checks and interpret cycle, voltage, and thermal indicators.",
    tools: ["Service app"],
    tags: ["battery", "preventive"],
  },
  {
    id: "lionsbot-navigation-sensor-clean",
    title: "Navigation Sensor Cleaning",
    manufacturer: "lionsbot",
    category: "maintenance",
    difficulty: "basic",
    minutes: 12,
    summary:
      "Clean LiDAR and front sensors to restore stable mapping and pathing accuracy.",
    tools: ["Lens cloth", "Air blower"],
    tags: ["sensor", "navigation"],
  },
  {
    id: "general-emergency-stop-diagnosis",
    title: "Emergency Stop Diagnosis",
    manufacturer: "general",
    category: "troubleshooting",
    difficulty: "advanced",
    minutes: 18,
    summary:
      "Diagnose false-stop conditions and isolate switch, harness, or controller causes.",
    tools: ["Multimeter", "Service manual"],
    tags: ["safety", "electrical"],
  },
];

const categoryIcon = {
  maintenance: <BuildIcon fontSize="small" />,
  repair: <HandymanIcon fontSize="small" />,
  troubleshooting: <PrecisionManufacturingIcon fontSize="small" />,
  calibration: <PrecisionManufacturingIcon fontSize="small" />,
};

const getEmbeddableUrl = (url) => {
  if (!url) return null;

  try {
    const parsed = new URL(url);

    if (
      parsed.hostname.includes("youtube.com") &&
      parsed.pathname === "/watch"
    ) {
      const videoId = parsed.searchParams.get("v");
      if (!videoId) return null;
      return `https://www.youtube.com/embed/${videoId}?controls=1&modestbranding=1&rel=0&fs=0&iv_load_policy=3&disablekb=1`;
    }

    if (parsed.hostname.includes("youtu.be")) {
      const videoId = parsed.pathname.replace("/", "").trim();
      if (!videoId) return null;
      return `https://www.youtube.com/embed/${videoId}?controls=1&modestbranding=1&rel=0&fs=0&iv_load_policy=3&disablekb=1`;
    }

    return url;
  } catch {
    return null;
  }
};

export default function GuidesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { guideId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isVideoPromoted, setIsVideoPromoted] = useState(false);
  const guideListRef = useRef(null);
  const guideContentRef = useRef(null);
  const videoFrameRef = useRef(null);
  const [leftListMaxHeight, setLeftListMaxHeight] = useState(null);
  const [showListBackToTop, setShowListBackToTop] = useState(false);

  const q = searchParams.get("q") || "";
  const manufacturer =
    searchParams.get("manufacturer") ||
    (guideId && !searchParams.get("manufacturer") ? guideId.toLowerCase() : "");
  const category = searchParams.get("category") || "";
  const difficulty = searchParams.get("difficulty") || "";
  const selectedGuideId = searchParams.get("guide") || "";

  const manufacturers = useMemo(
    () => [...new Set(GUIDE_ITEMS.map((item) => item.manufacturer).sort())],
    [],
  );

  const setQuery = (updates) => {
    const next = new URLSearchParams(searchParams);

    Object.entries(updates).forEach(([key, value]) => {
      if (value == null || value === "") {
        next.delete(key);
      } else {
        next.set(key, value);
      }
    });

    setSearchParams(next);
  };

  const filteredGuides = useMemo(() => {
    const search = q.trim().toLowerCase();
    return GUIDE_ITEMS.filter((item) => {
      const matchesManufacturer =
        !manufacturer || item.manufacturer === manufacturer;
      const matchesCategory = !category || item.category === category;
      const matchesDifficulty = !difficulty || item.difficulty === difficulty;

      const matchesSearch =
        !search ||
        item.title.toLowerCase().includes(search) ||
        item.summary.toLowerCase().includes(search) ||
        item.tags.some((tag) => tag.toLowerCase().includes(search));

      return (
        matchesManufacturer &&
        matchesCategory &&
        matchesDifficulty &&
        matchesSearch
      );
    });
  }, [category, difficulty, manufacturer, q]);

  const selectedGuide = filteredGuides.find(
    (guide) => guide.id === selectedGuideId,
  );
  const selectedGuideEmbedUrl = getEmbeddableUrl(selectedGuide?.contentUrl);

  const compactFilterFieldSx = {
    "& .MuiInputBase-root": {
      height: 40,
    },
  };

  const handleExpandVideo = () => {
    setIsVideoPromoted(true);
  };

  const handleRestoreVideo = () => {
    setIsVideoPromoted(false);
  };

  useEffect(() => {
    if (!guideContentRef.current) return;

    const updateHeight = () => {
      const height = guideContentRef.current?.offsetHeight;
      if (height) {
        setLeftListMaxHeight(height);
      }
    };

    updateHeight();

    const observer = new ResizeObserver(updateHeight);
    observer.observe(guideContentRef.current);

    window.addEventListener("resize", updateHeight);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateHeight);
    };
  }, [selectedGuideId, q, manufacturer, category, difficulty]);

  useEffect(() => {
    if (!isVideoPromoted) return;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isVideoPromoted]);

  useEffect(() => {
    const updateBackToTopVisibility = () => {
      const node = guideListRef.current;
      if (!node) return;

      const needsScroll = node.scrollHeight > node.clientHeight + 1;
      const firstItem = node.querySelector("[data-guide-item='true']");
      const oneItemHeight = firstItem
        ? firstItem.getBoundingClientRect().height
        : 120;

      setShowListBackToTop(needsScroll && node.scrollTop >= oneItemHeight);
    };

    const node = guideListRef.current;

    updateBackToTopVisibility();

    if (node) {
      node.addEventListener("scroll", updateBackToTopVisibility);
    }

    window.addEventListener("resize", updateBackToTopVisibility);

    return () => {
      if (node) {
        node.removeEventListener("scroll", updateBackToTopVisibility);
      }
      window.removeEventListener("resize", updateBackToTopVisibility);
    };
  }, [filteredGuides, leftListMaxHeight]);

  return (
    <>
      <TopNavBar heading={"Guides"} />
      <Container maxWidth={false} sx={{ py: 4, px: { xs: 2, md: 4 } }}>
        <Box sx={{ mb: 3 }}>
          <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
            {user?.role === "control" && (
              <IconButton
                aria-label="Back to controls"
                onClick={() => navigate("/device-controls")}
                sx={{
                  border: "1px solid",
                  borderColor: "primary.main",
                  backgroundColor: "common.white",
                  color: "primary.main",
                  width: 52,
                  height: 52,
                  boxShadow: 2,
                  "&:hover": {
                    backgroundColor: "primary.main",
                    color: "common.white",
                  },
                }}
              >
                <Stack
                  direction="row"
                  spacing={0.35}
                  sx={{ alignItems: "center" }}
                >
                  <SettingsRemoteIcon sx={{ fontSize: 20 }} />
                  <ArrowBackIcon sx={{ fontSize: 20 }} />
                </Stack>
              </IconButton>
            )}
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Robot Maintenance and Repair Guides
            </Typography>
          </Stack>
          <Typography color="text.secondary" sx={{ mt: 0.5 }}>
            Find step-by-step tutorials by manufacturer, category, difficulty,
            and issue.
          </Typography>
        </Box>

        <Paper
          variant="outlined"
          sx={{
            p: 1.25,
            mb: 2,
            maxWidth: { xs: "100%", lg: 980 },
          }}
        >
          <Grid container spacing={1}>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                size="small"
                fullWidth
                label="Search guides"
                value={q}
                onChange={(e) => setQuery({ q: e.target.value })}
                placeholder="Search by issue, task, or keyword"
                sx={compactFilterFieldSx}
                InputProps={{
                  sx: { fontSize: "0.9rem" },
                  startAdornment: (
                    <SearchIcon
                      sx={{ mr: 0.75, color: "text.secondary", fontSize: 18 }}
                    />
                  ),
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4, md: 2.67 }}>
              <TextField
                size="small"
                select
                fullWidth
                label="Manufacturer"
                value={manufacturer}
                sx={compactFilterFieldSx}
                onChange={(e) =>
                  setQuery({ manufacturer: e.target.value, guide: "" })
                }
              >
                <MenuItem value="">All</MenuItem>
                {manufacturers.map((name) => (
                  <MenuItem key={name} value={name}>
                    {name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 4, md: 2.67 }}>
              <TextField
                size="small"
                select
                fullWidth
                label="Category"
                value={category}
                sx={compactFilterFieldSx}
                onChange={(e) =>
                  setQuery({ category: e.target.value, guide: "" })
                }
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="maintenance">Maintenance</MenuItem>
                <MenuItem value="repair">Repair</MenuItem>
                <MenuItem value="troubleshooting">Troubleshooting</MenuItem>
                <MenuItem value="calibration">Calibration</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 4, md: 2.66 }}>
              <TextField
                size="small"
                select
                fullWidth
                label="Difficulty"
                value={difficulty}
                sx={compactFilterFieldSx}
                onChange={(e) =>
                  setQuery({ difficulty: e.target.value, guide: "" })
                }
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="basic">Basic</MenuItem>
                <MenuItem value="technician">Technician</MenuItem>
                <MenuItem value="advanced">Advanced</MenuItem>
              </TextField>
            </Grid>
          </Grid>

          <Stack
            direction="row"
            spacing={1}
            sx={{ mt: 1, flexWrap: "wrap", rowGap: 0.75 }}
          >
            <Chip
              label={`${filteredGuides.length} results`}
              size="small"
              color="primary"
              variant="outlined"
            />
            {manufacturer && (
              <Chip
                label={`Scoped: ${manufacturer}`}
                size="small"
                onDelete={() => setQuery({ manufacturer: "", guide: "" })}
              />
            )}
            {(q || category || difficulty) && (
              <Button
                size="small"
                sx={{ minHeight: 24, px: 1 }}
                onClick={() =>
                  setQuery({ q: "", category: "", difficulty: "", guide: "" })
                }
              >
                Clear filters
              </Button>
            )}
          </Stack>
        </Paper>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 5 }}>
            <Box
              sx={{
                pr: { md: 1 },
                maxHeight: {
                  md: leftListMaxHeight
                    ? `${leftListMaxHeight}px`
                    : "calc(100vh - 190px)",
                },
                overflowY: "auto",
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": {
                  display: "none",
                },
              }}
              ref={guideListRef}
            >
              <Grid container spacing={1.5}>
                {filteredGuides.map((guide) => (
                  <Grid key={guide.id} size={{ xs: 12 }}>
                    <Card
                      data-guide-item="true"
                      variant="outlined"
                      onClick={() => setQuery({ guide: guide.id })}
                      sx={{
                        height: "100%",
                        borderRadius: 2,
                        cursor: "pointer",
                        borderColor:
                          selectedGuideId === guide.id
                            ? "primary.main"
                            : undefined,
                        boxShadow: selectedGuideId === guide.id ? 2 : undefined,
                      }}
                    >
                      <CardContent>
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{ mb: 1, alignItems: "center" }}
                        >
                          {categoryIcon[guide.category] || (
                            <BuildIcon fontSize="small" />
                          )}
                          <Typography
                            variant="h6"
                            sx={{
                              fontSize: "1rem",
                              fontWeight: 700,
                              flexGrow: 1,
                            }}
                          >
                            {guide.title}
                          </Typography>
                          <IconButton
                            size="small"
                            aria-label="Open guide"
                            onClick={(e) => {
                              e.stopPropagation();
                              setQuery({ guide: guide.id });
                            }}
                          >
                            <OpenInNewIcon fontSize="inherit" />
                          </IconButton>
                        </Stack>

                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 1.5, lineHeight: 1.5 }}
                        >
                          {guide.summary}
                        </Typography>

                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{ mb: 1.5, flexWrap: "wrap", rowGap: 0.75 }}
                        >
                          <Chip size="small" label={guide.manufacturer} />
                          <Chip
                            size="small"
                            label={guide.category}
                            variant="outlined"
                          />
                          <Chip
                            size="small"
                            label={guide.difficulty}
                            color="secondary"
                            variant="outlined"
                          />
                          {guide.contentType === "video" && (
                            <Chip
                              size="small"
                              label="Video"
                              color="info"
                              variant="outlined"
                            />
                          )}
                        </Stack>

                        <Stack
                          direction="row"
                          spacing={0.75}
                          sx={{ alignItems: "center" }}
                        >
                          <AccessTimeIcon
                            sx={{ fontSize: 16, color: "text.secondary" }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {guide.minutes} min
                          </Typography>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}

                {filteredGuides.length === 0 && (
                  <Grid size={{ xs: 12 }}>
                    <Paper variant="outlined" sx={{ p: 3 }}>
                      <Typography>
                        No guides found for current filters.
                      </Typography>
                    </Paper>
                  </Grid>
                )}
              </Grid>

              <Box
                sx={{
                  position: "sticky",
                  bottom: 10,
                  display: "flex",
                  justifyContent: "center",
                  pt: 1,
                  pb: 0.5,
                  mt: 1.5,
                  background: "transparent",
                }}
              >
                <Box
                  sx={{
                    opacity: showListBackToTop ? 1 : 0,
                    transform: showListBackToTop
                      ? "translateY(0)"
                      : "translateY(6px)",
                    transition: "opacity 180ms ease, transform 180ms ease",
                    pointerEvents: showListBackToTop ? "auto" : "none",
                  }}
                >
                  <IconButton
                    aria-label="Back to top"
                    onClick={() =>
                      guideListRef.current?.scrollTo({
                        top: 0,
                        behavior: "smooth",
                      })
                    }
                    sx={{
                      border: "1px solid",
                      borderColor: "primary.main",
                      backgroundColor: "common.white",
                      color: "primary.main",
                      width: 52,
                      height: 52,
                      boxShadow: 2,
                      "&:hover": {
                        backgroundColor: "primary.main",
                        color: "common.white",
                      },
                    }}
                  >
                    <VerticalAlignTopIcon sx={{ fontSize: 30 }} />
                  </IconButton>
                </Box>
              </Box>
            </Box>
          </Grid>

          <Grid size={{ xs: 12, md: 7 }}>
            <Paper
              ref={guideContentRef}
              variant="outlined"
              sx={{
                p: 3,
                minHeight: { md: 960 },
                position: { md: "sticky" },
                top: 16,
              }}
            >
              <Typography variant="h6" sx={{ mb: 1 }}>
                {selectedGuide ? selectedGuide.title : "Guide Details"}
              </Typography>

              {!selectedGuide ? (
                <Typography color="text.secondary">
                  Select a guide card to view required tools, expected duration,
                  and execution notes.
                </Typography>
              ) : (
                <>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1.5 }}
                  >
                    {selectedGuide.summary}
                  </Typography>
                  <Divider sx={{ mb: 1.5 }} />
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Required Tools
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ mb: 2, flexWrap: "wrap", rowGap: 0.75 }}
                  >
                    {selectedGuide.tools.map((tool) => (
                      <Chip
                        key={tool}
                        size="small"
                        label={tool}
                        variant="outlined"
                      />
                    ))}
                  </Stack>

                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Suggested Steps
                  </Typography>
                  <Box component="ol" sx={{ pl: 2, mt: 0 }}>
                    <Typography
                      component="li"
                      variant="body2"
                      sx={{ mb: 0.75 }}
                    >
                      Perform safety checks and isolate the robot power as
                      required.
                    </Typography>
                    <Typography
                      component="li"
                      variant="body2"
                      sx={{ mb: 0.75 }}
                    >
                      Follow manufacturer-specific maintenance or repair
                      sequence.
                    </Typography>
                    <Typography component="li" variant="body2">
                      Validate robot state in controls and confirm no active
                      faults remain.
                    </Typography>
                  </Box>

                  {selectedGuideEmbedUrl && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Tutorial Video
                      </Typography>
                      <Box
                        sx={{
                          position: "relative",
                          width: "100%",
                          overflow: "visible",
                          mb: 1.5,
                        }}
                      >
                        <IconButton
                          aria-label="Open video in large view"
                          onClick={handleExpandVideo}
                          disabled={isVideoPromoted}
                          sx={{
                            position: "absolute",
                            top: 0,
                            right: 14,
                            transform: "translateY(-50%)",
                            zIndex: 2,
                            backgroundColor: "common.white",
                            color: "primary.main",
                            border: "1px solid",
                            borderColor: "primary.main",
                            width: 52,
                            height: 52,
                            boxShadow: 2,
                            "&:hover": {
                              backgroundColor: "primary.main",
                              color: "common.white",
                            },
                          }}
                        >
                          <FullscreenIcon sx={{ fontSize: 30 }} />
                        </IconButton>

                        <Box
                          ref={videoFrameRef}
                          sx={{
                            position: isVideoPromoted ? "fixed" : "relative",
                            top: isVideoPromoted ? "8vh" : "auto",
                            left: isVideoPromoted ? "6vw" : "auto",
                            width: isVideoPromoted ? "88vw" : "100%",
                            height: isVideoPromoted ? "84vh" : "auto",
                            paddingTop: isVideoPromoted ? 0 : "56.25%",
                            borderRadius: 1,
                            overflow: "hidden",
                            border: "1px solid",
                            borderColor: "divider",
                            zIndex: isVideoPromoted ? 3000 : "auto",
                            backgroundColor: "black",
                            boxShadow: isVideoPromoted
                              ? "0 0 0 100vmax rgba(0, 0, 0, 0.72)"
                              : "none",
                          }}
                        >
                          {isVideoPromoted && (
                            <IconButton
                              aria-label="Restore video position"
                              onClick={handleRestoreVideo}
                              sx={{
                                position: "absolute",
                                top: 10,
                                right: 10,
                                zIndex: 3100,
                                backgroundColor: "common.white",
                                color: "primary.main",
                                border: "1px solid",
                                borderColor: "primary.main",
                                width: 44,
                                height: 44,
                                "&:hover": {
                                  backgroundColor: "primary.main",
                                  color: "common.white",
                                },
                              }}
                            >
                              <CloseIcon />
                            </IconButton>
                          )}

                          <Box
                            component="iframe"
                            src={selectedGuideEmbedUrl}
                            title={`${selectedGuide.title} video`}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            allowFullScreen
                            sx={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              width: "100%",
                              height: "100%",
                              border: 0,
                            }}
                          />
                        </Box>
                      </Box>
                    </>
                  )}
                </>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </>
  );
}
